import { useState, useEffect, FormEvent } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { supabase } from '../lib/supabase'
import { Heart, Lock, Loader2, AlertCircle, ChevronDown, User } from 'lucide-react'

interface UserOption {
  display_name: string
  email: string
}

export function Login() {
  const { login } = useAuth()
  const [users, setUsers] = useState<UserOption[]>([])
  const [selectedEmail, setSelectedEmail] = useState('')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(true)
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [loadingUsers, setLoadingUsers] = useState(true)

  // Load user list for dropdown
  useEffect(() => {
    async function loadUsers(attempt = 1): Promise<void> {
      try {
        // Use direct fetch() instead of supabase.rpc() because
        // the Supabase JS client hangs in Electron (never resolves)
        const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
        const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY

        const controller = new AbortController()
        const timeoutId = setTimeout(() => controller.abort(), 15000)

        const response = await fetch(`${supabaseUrl}/rest/v1/rpc/get_user_list`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'apikey': supabaseKey,
            'Authorization': `Bearer ${supabaseKey}`
          },
          body: '{}',
          signal: controller.signal
        })

        clearTimeout(timeoutId)

        if (response.ok) {
          const rpcData = await response.json()
          const dataArray = Array.isArray(rpcData) ? rpcData : [rpcData]
          const validUsers = (dataArray as UserOption[]).filter(u => u.email)
          if (validUsers.length > 0) {
            setUsers(validUsers)
            setLoadingUsers(false)
            return
          }
        } else {
          const errBody = await response.text()
          console.warn(`RPC get_user_list HTTP ${response.status}: ${errBody}`)
        }

        // Retry if we have attempts left
        if (attempt < 3) {
          console.log(`Retrying get_user_list (attempt ${attempt + 1}/3)...`)
          await new Promise(r => setTimeout(r, 2000))
          return loadUsers(attempt + 1)
        }
      } catch (e: any) {
        console.error(`Failed to load user list (attempt ${attempt}):`, e?.message || e)
        if (attempt < 3) {
          await new Promise(r => setTimeout(r, 2000))
          return loadUsers(attempt + 1)
        }
      } finally {
        setLoadingUsers(false)
      }
    }
    loadUsers()
  }, [])

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault()
    setError('')
    if (!selectedEmail) {
      setError('Seleziona un utente')
      return
    }
    setIsLoading(true)

    const result = await login(selectedEmail, password, rememberMe)
    if (result.error) {
      setError(result.error === 'Invalid login credentials'
        ? 'Password non valida'
        : result.error)
    }
    setIsLoading(false)
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-900 via-purple-900 to-slate-900 flex items-center justify-center p-4">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-indigo-600/20 rounded-full blur-3xl" />
        <div className="absolute bottom-1/4 right-1/4 w-80 h-80 bg-purple-600/20 rounded-full blur-3xl" />
      </div>

      <div className="relative w-full max-w-md">
        {/* Logo & title */}
        <div className="text-center mb-8 animate-fade-in">
          <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-indigo-500 to-purple-600 rounded-2xl shadow-2xl shadow-indigo-500/30 mb-4">
            <Heart className="w-10 h-10 text-white" />
          </div>
          <h1 className="text-3xl font-black text-white tracking-tight">
            Gestione Infermieri
          </h1>
          <p className="text-indigo-200/70 mt-2 text-sm">
            Seleziona il tuo profilo e accedi
          </p>
        </div>

        {/* Login form */}
        <form
          onSubmit={handleSubmit}
          className="bg-white/10 backdrop-blur-xl rounded-2xl p-8 shadow-2xl border border-white/10 animate-fade-in"
          style={{ animationDelay: '0.1s' }}
        >
          {error && (
            <div className="mb-4 p-3 bg-red-500/20 border border-red-400/30 rounded-xl flex items-center gap-2 text-red-200 text-sm">
              <AlertCircle className="w-4 h-4 flex-shrink-0" />
              {error}
            </div>
          )}

          <div className="space-y-4">
            {/* User dropdown */}
            <div>
              <label className="block text-xs font-bold text-indigo-200 mb-1.5 uppercase tracking-wider">
                Utente
              </label>
              <div className="relative">
                <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-indigo-300/50" />
                {loadingUsers ? (
                  <div className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/10 rounded-xl text-indigo-300/50 text-sm flex items-center gap-2">
                    <Loader2 className="w-4 h-4 animate-spin" /> Caricamento utenti...
                  </div>
                ) : users.length > 0 && users.some(u => u.email) ? (
                  <div className="relative">
                    <select
                      value={selectedEmail}
                      onChange={e => setSelectedEmail(e.target.value)}
                      className="w-full pl-10 pr-10 py-3 bg-white/10 border border-white/10 rounded-xl text-white text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all appearance-none cursor-pointer"
                    >
                      <option value="" className="bg-slate-800 text-white">Seleziona utente...</option>
                      {users.map(u => (
                        <option key={u.email} value={u.email} className="bg-slate-800 text-white">
                          {u.display_name}
                        </option>
                      ))}
                    </select>
                    <ChevronDown className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-indigo-300/50 pointer-events-none" />
                  </div>
                ) : (
                  /* Fallback: type email manually if RPC not available */
                  <input
                    type="email"
                    value={selectedEmail}
                    onChange={e => setSelectedEmail(e.target.value)}
                    required
                    placeholder="nome@esempio.it"
                    className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/10 rounded-xl text-white placeholder-indigo-300/30 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                  />
                )}
              </div>
            </div>

            {/* Password */}
            <div>
              <label className="block text-xs font-bold text-indigo-200 mb-1.5 uppercase tracking-wider">
                Password
              </label>
              <div className="relative">
                <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-indigo-300/50" />
                <input
                  type="password"
                  value={password}
                  onChange={e => setPassword(e.target.value)}
                  required
                  placeholder="••••••••"
                  className="w-full pl-10 pr-4 py-3 bg-white/10 border border-white/10 rounded-xl text-white placeholder-indigo-300/30 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition-all"
                />
              </div>
              <p className="text-[11px] text-indigo-300/40 mt-1.5 italic">
                Prima volta? Usa: Gestione.infermieri.2026
              </p>
            </div>

            {/* Remember me */}
            <label className="flex items-center gap-3 cursor-pointer group">
              <div className="relative">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={e => setRememberMe(e.target.checked)}
                  className="sr-only peer"
                />
                <div className="w-5 h-5 rounded-md border-2 border-white/20 bg-white/5 peer-checked:bg-indigo-500 peer-checked:border-indigo-500 transition-all flex items-center justify-center">
                  {rememberMe && (
                    <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                    </svg>
                  )}
                </div>
              </div>
              <span className="text-sm text-indigo-200/70 group-hover:text-indigo-200 transition-colors">Resta connesso</span>
            </label>
          </div>

          <button
            type="submit"
            disabled={isLoading}
            className="w-full mt-6 py-3 bg-gradient-to-r from-indigo-600 to-purple-600 text-white font-bold rounded-xl shadow-lg hover:shadow-xl hover:from-indigo-500 hover:to-purple-500 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {isLoading ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Accesso in corso...
              </>
            ) : (
              'Accedi'
            )}
          </button>
        </form>

        <p className="text-center text-indigo-300/30 text-xs mt-6">
          Gestione Infermieri v1.0 — QuiCare Srl
        </p>
      </div>
    </div>
  )
}
